import { getInitials } from '../../../shared/utils';
import type {
  CardActivityModel,
  CardActivityType,
  CardAttachmentModel,
  CardChecklistModel,
  CardCommentModel,
  CardFieldValueModel,
  CardOwnerOptionModel,
  CardStageOptionModel,
} from '../models/card-details-api.models';
import type {
  CardActivityViewModel,
  CardAttachmentViewModel,
  CardChecklistViewModel,
  CardCommentViewModel,
} from '../models/card-details.model';

export function mapChecklistToViewModel(checklist: CardChecklistModel): CardChecklistViewModel {
  return {
    id: checklist.id,
    cardId: checklist.cardId,
    title: checklist.title,
    order: checklist.order,
    items: checklist.items ?? [],
  };
}

export function mapCommentToViewModel(
  comment: CardCommentModel,
  usersMap: Map<string, CardOwnerOptionModel>
): CardCommentViewModel {
  const owner = usersMap.get(comment.userId);
  const externalReply = comment.isExternalReply === true;
  const userName = externalReply ? 'WhatsApp' : owner?.name || '-';
  const fromComment = (comment.userAvatarUrl ?? '').trim();
  const fromOwner = (owner?.avatarUrl ?? '').trim();
  const userAvatarUrl = fromComment || fromOwner || null;
  return {
    id: comment.id,
    cardId: comment.cardId,
    userId: comment.userId,
    userName,
    isExternalReply: externalReply,
    userInitials: getInitials(userName, 'US'),
    userAvatarUrl: externalReply ? null : userAvatarUrl,
    content: comment.content,
    isEdited: comment.isEdited,
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
  };
}

export function mapAttachmentToViewModel(attachment: CardAttachmentModel): CardAttachmentViewModel {
  return {
    id: attachment.id,
    cardId: attachment.cardId,
    uploadedByUserId: attachment.uploadedByUserId,
    fileName: attachment.fileName,
    originalFileName: attachment.originalFileName,
    contentType: attachment.contentType,
    extension: attachment.extension,
    fileSize: attachment.fileSize,
    uploadedAt: attachment.uploadedAt,
    url: attachment.url ?? null,
    assetUrl: attachment.assetUrl ?? null,
    fileUrl: attachment.fileUrl ?? null,
    path: attachment.path ?? null,
  };
}

export function mapActivityToViewModel(activity: CardActivityModel): CardActivityViewModel {
  return {
    id: activity.id,
    cardId: activity.cardId,
    activityType: activity.activityType,
    activityTypeLabel: activityTypeToLabel(activity.activityType),
    description: activity.description,
    performedByUserId: activity.performedByUserId,
    createdAt: activity.createdAt,
  };
}

export function toFieldValueMap(values: CardFieldValueModel[]): Map<string, CardFieldValueModel['value']> {
  return new Map(values.map((item) => [item.workflowFieldId, item.value] as const));
}

export function resolveStageName(
  stageId: string,
  stageOptions: CardStageOptionModel[],
  fallback = '-'
): string {
  return stageOptions.find((stage) => stage.id === stageId)?.name || fallback;
}

function activityTypeToLabel(type: CardActivityType): string {
  switch (type) {
    case 1:
      return 'cards.history.types.commentcreated';
    case 2:
      return 'cards.history.types.commentupdated';
    case 3:
      return 'cards.history.types.commentdeleted';
    case 4:
      return 'cards.history.types.checklistcreated';
    case 5:
      return 'cards.history.types.checklistupdated';
    case 6:
      return 'cards.history.types.checklistdeleted';
    case 7:
      return 'cards.history.types.checklistitemcreated';
    case 8:
      return 'cards.history.types.checklistitemupdated';
    case 9:
      return 'cards.history.types.checklistitemtoggled';
    case 10:
      return 'cards.history.types.checklistitemdeleted';
    case 11:
      return 'cards.history.types.attachmentuploaded';
    case 12:
      return 'cards.history.types.attachmentdeleted';
    default:
      return 'cards.history.types.activity';
  }
}
